import { ComboboxData } from '@mantine/core';
import {
  companyComboBoxAdapter,
  CompanyComboBoxData,
  userComboBoxAdapter,
  UserComboBoxData,
} from 'adapters/session.adapter';
import useFetchAndLoad from 'hooks/useFetchAndLoad';
import { createContext, useState } from 'react';

import {
  getCoachesInput,
  getCoachGuestsInput,
  getCoacheesInput,
  getCompaniesInput,
} from 'services/nexus';
import { getCoacheeComboData, getUsersClaraComboData } from 'services/input.service';
import { getUnifiedCoaches } from 'services/coach.service';

interface AdvanceSearchContextProps {
  comboDataCoaches: ComboboxData;
  comboDataCoachees: ComboboxData;
  comboDataCompanies: ComboboxData;
  comboDataMentors: ComboboxData;
  comboDataMentees: ComboboxData;
  comboDataUsersClara: ComboboxData;
  getComboDataCoaches: (params: { refetch: boolean }) => Promise<ComboboxData>;
  getComboDataMentors: (params: { refetch: boolean }) => Promise<ComboboxData>;
  getComboDataCoachees: (params: { refetch: boolean }) => Promise<ComboboxData>;
  getComboDataMentees: (params: { refetch: boolean }) => Promise<ComboboxData>;
  getComboDataCompanies: (params: { refetch: boolean }) => Promise<ComboboxData>;
  getComboDataUsersClara: (params: { refetch: boolean }) => Promise<ComboboxData>;
  initializeComboData: (params: {
    fields: {
      coaches?: boolean;
      mentors?: boolean;
      coachees?: boolean;
      mentees?: boolean;
      companies?: boolean;
      usersClara?: boolean;
    };
    refetch?: boolean;
  }) => Promise<void>;
  loading: boolean;
}

export const AdvanceSearchContext = createContext<AdvanceSearchContextProps>({
  comboDataCoaches: [],
  comboDataCoachees: [],
  comboDataCompanies: [],
  comboDataMentors: [],
  comboDataMentees: [],
  comboDataUsersClara: [],
  getComboDataCoaches: async () => [],
  getComboDataMentors: async () => [],
  getComboDataCoachees: async () => [],
  getComboDataMentees: async () => [],
  getComboDataCompanies: async () => [],
  getComboDataUsersClara: async () => [],
  initializeComboData: async () => Promise.resolve(),
  loading: false,
});

export const AdvanceSearchProvider = (props: React.PropsWithChildren<{}>) => {
  const { callEndpoint, loading } = useFetchAndLoad();
  const [comboDataCoaches, setComboDataCoaches] = useState<ComboboxData>([]);
  const [comboDataCoachees, setComboDataCoachees] = useState<ComboboxData>([]);
  const [comboDataCompanies, setComboDataCompanies] = useState<ComboboxData>([]);
  const [comboDataMentors, setComboDataMentors] = useState<ComboboxData>([]);
  const [comboDataMentees, setComboDataMentees] = useState<ComboboxData>([]);
  const [comboDataUsersClara, setComboDataUsersClara] = useState<ComboboxData>([]);

  const fetchCoaches = async (): Promise<UserComboBoxData[]> => {
    const [coachesRes, coachGuestsRes] = await Promise.all([
      callEndpoint(getCoachesInput()),
      callEndpoint(getCoachGuestsInput()),
    ]);
    const coaches = coachesRes.data?.data || [];
    const coachGuests = coachGuestsRes.data?.data || [];
    return [...coaches, ...coachGuests];
  };

  const fetchMentors = async (): Promise<UserComboBoxData[]> => {
    const { data } = await callEndpoint(
      getUnifiedCoaches({ page: 1, pageSize: 1000 }, 'mentoring'),
    );
    return data.data?.data || [];
  };

  const fetchCoachees = async (): Promise<UserComboBoxData[]> => {
    const { data } = await callEndpoint(getCoacheesInput());
    return data.data || [];
  };

  const fetchMentees = async (): Promise<UserComboBoxData[]> => {
    const { data } = await callEndpoint(getCoacheeComboData('mentoring'));
    return data.data || [];
  };

  const fetchCompanies = async (): Promise<CompanyComboBoxData[]> => {
    const { data } = await callEndpoint(getCompaniesInput());
    return data.data || [];
  };

  const fetchUsersClara = async (): Promise<UserComboBoxData[]> => {
    const { data } = await callEndpoint(getUsersClaraComboData());
    return data.data || [];
  };

  const getComboDataCoaches = async ({ refetch }: { refetch: boolean }) => {
    if (refetch || comboDataCoaches.length === 0) {
      const fetchedCoaches = await fetchCoaches();
      const newCoaches = userComboBoxAdapter(fetchedCoaches);
      setComboDataCoaches(newCoaches);
      return newCoaches;
    }
    return comboDataCoaches;
  };

  const getComboDataMentors = async ({ refetch }: { refetch: boolean }) => {
    if (refetch || comboDataMentors.length === 0) {
      const fetchedMentors = await fetchMentors();
      const newMentors = userComboBoxAdapter(fetchedMentors);
      setComboDataMentors(newMentors);
      return newMentors;
    }
    return comboDataMentors;
  };

  const getComboDataCoachees = async ({ refetch }: { refetch: boolean }) => {
    if (refetch || comboDataCoachees.length === 0) {
      const fetchedCoachees = await fetchCoachees();
      const newCoachees = userComboBoxAdapter(fetchedCoachees);
      setComboDataCoachees(newCoachees);
      return newCoachees;
    }
    return comboDataCoachees;
  };

  const getComboDataMentees = async ({ refetch }: { refetch: boolean }) => {
    if (refetch || comboDataMentees.length === 0) {
      const fetchedMentees = await fetchMentees();
      const newMentees = userComboBoxAdapter(fetchedMentees);
      setComboDataMentees(newMentees);
      return newMentees;
    }
    return comboDataMentees;
  };

  const getComboDataCompanies = async ({ refetch }: { refetch: boolean }) => {
    if (refetch || comboDataCompanies.length === 0) {
      const fetchedCompanies = await fetchCompanies();
      const newCompanies = companyComboBoxAdapter(fetchedCompanies);
      setComboDataCompanies(newCompanies);
      return newCompanies;
    }
    return comboDataCompanies;
  };

  const getComboDataUsersClara = async ({ refetch }: { refetch: boolean }) => {
    if (refetch || comboDataUsersClara.length === 0) {
      const fetchedUsersClara = await fetchUsersClara();
      const newUsersClara = userComboBoxAdapter(fetchedUsersClara);
      setComboDataUsersClara(newUsersClara);
      return newUsersClara;
    }
    return comboDataUsersClara;
  };

  const initializeComboData = async ({
    fields,
    refetch = false,
  }: {
    fields: {
      coaches?: boolean;
      mentors?: boolean;
      coachees?: boolean;
      mentees?: boolean;
      companies?: boolean;
      usersClara?: boolean;
    };
    refetch?: boolean;
  }) => {
    if (fields.coaches) await getComboDataCoaches({ refetch });
    if (fields.mentors) await getComboDataMentors({ refetch });
    if (fields.coachees) await getComboDataCoachees({ refetch });
    if (fields.mentees) await getComboDataMentees({ refetch });
    if (fields.companies) await getComboDataCompanies({ refetch });
    if (fields.usersClara) await getComboDataUsersClara({ refetch });
  };

  return (
    <AdvanceSearchContext.Provider
      value={{
        comboDataCoaches,
        comboDataCoachees,
        comboDataCompanies,
        comboDataMentors,
        comboDataMentees,
        comboDataUsersClara,
        getComboDataCoaches,
        getComboDataMentors,
        getComboDataCoachees,
        getComboDataMentees,
        getComboDataCompanies,
        getComboDataUsersClara,
        initializeComboData,
        loading,
      }}
    >
      {props.children}
    </AdvanceSearchContext.Provider>
  );
};
